package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import dev.lingvo.shared.BindStoreAppRequest
import dev.lingvo.shared.StoreConnectionDto
import dev.lingvo.shared.StorePlatform
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/** All requests retain StoreActionRunner's captured actor, workspace, project and authority checks. */
@Composable
internal fun StoreConnectionWizard(
    platform: StorePlatform,
    initial: StoreConnectionDto?,
    data: StoresPageData,
    runner: StoreActionRunner,
    onBusy: (Boolean) -> Unit,
    onBound: () -> Unit,
) {
    var connection by remember(platform, initial?.id) { mutableStateOf(initial) }
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    fun setBusy(value: Boolean) { busy = value; onBusy(value) }
    fun fail(failure: Throwable): Boolean {
        error = storeErrorMessage(failure, tr("app.projects.slug.store_connection_wizard.the_check_didnt_finish_try_again", "The check didn't finish. Try again."))
        return true
    }
    fun bind(ref: String, name: String?) {
        val chosen = connection ?: return
        error = null
        runner.run(
            setBusy = ::setBusy, failure = tr("app.projects.slug.store_connection_wizard.couldnt_connect_this_app_try_again", "Couldn't connect this app. Try again."),
            transport = { ApiClient.bindStoreApp(data.project.slug, platform, BindStoreAppRequest(chosen.id, ref, name)) },
            onError = ::fail,
        ) { onBound() }
    }
    Column(Modifier.fillMaxWidth().gap(Space.MD).attr("data-store-wizard", platform.id)) {
        LSectionTitle(
            if (connection == null) t("app.projects.slug.store_connection_wizard.1_connect_your_account", "1. Connect your account")
            else t("app.projects.slug.store_connection_wizard.2_verify_your_app", "2. Verify your app"),
        )
        Div(attrs = { attr("role", "status"); attr("aria-live", "polite") }) {
            if (busy) Text(t("app.projects.slug.store_connection_wizard.checking_with_the_store", "Checking with the store…"))
        }
        error?.let {
            LAlert(t("app.projects.slug.store_connection_wizard.connection_needs_attention", "Connection needs attention"), it, variant = LAlertVariant.DANGER, live = true)
        }
        val chosen = connection
        if (chosen == null) {
            StoreCredentialStep(platform, busy) { request ->
                error = null
                runner.run(
                    setBusy = ::setBusy, failure = tr("app.projects.slug.store_connection_wizard.couldnt_verify_the_connection_try_again", "Couldn't verify the connection. Try again."),
                    transport = { ApiClient.createStoreConnection(data.orgSlug, request) }, onError = ::fail,
                ) { connection = it }
            }
        } else if (platform == StorePlatform.APP_STORE) {
            StoreAppleWizard(chosen, data.orgSlug, runner, busy, ::setBusy, { error = null }, ::fail,
                onAllowed = { connection = it }, onBind = ::bind)
        } else {
            StorePlayWizard(chosen, data.orgSlug, runner, busy, ::setBusy, { error = null }, ::fail, onBind = { bind(it, null) })
        }
    }
}
